import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import './Wishlist.css';

const Wishlist = () => {
  const navigate = useNavigate();
  const { wishlist, removeFromWishlist, clearWishlist } = useWishlist();
  const { addToCart } = useCart();

  const handleAddToCart = (product) => {
    addToCart(product);
    // Remove from wishlist when added to cart
    removeFromWishlist(product.id);
  };

  const handleRemoveFromWishlist = (productId) => {
    if (window.confirm('Remove this item from your wishlist?')) {
      removeFromWishlist(productId);
    }
  };

  const handleClearWishlist = () => {
    if (window.confirm('Clear your entire wishlist? This action cannot be undone.')) {
      clearWishlist();
    }
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`);
  };

  if (wishlist.items.length === 0) {
    return (
      <div className="wishlist-page">
        <div className="container">
          <div className="wishlist-empty">
            <div className="empty-icon">❤️</div>
            <h1>Your Wishlist is Empty</h1>
            <p>Start adding products you love to your wishlist!</p>
            <button onClick={() => navigate('/products')} className="btn btn-primary">
              Browse Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <div className="container">
        <div className="wishlist-header">
          <h1>My Wishlist</h1>
          <p>You have {wishlist.items.length} item{wishlist.items.length !== 1 ? 's' : ''} in your wishlist</p>
        </div>

        <div className="wishlist-actions">
          <button onClick={() => navigate('/products')} className="btn btn-secondary">
            Continue Shopping
          </button>
          {wishlist.items.length > 0 && (
            <button onClick={handleClearWishlist} className="btn btn-danger">
              Clear Wishlist
            </button>
          )}
        </div>

        <div className="wishlist-items">
          {wishlist.items.map((item) => (
            <div key={item.id} className="wishlist-item">
              <div className="item-image" onClick={() => handleProductClick(item)}>
                <img 
                  src={item.primary_image || item.image || '/images/placeholder.jpg'} 
                  alt={item.name}
                  onError={(e) => {
                    e.target.src = '/images/placeholder.jpg';
                  }}
                />
              </div>
              
              <div className="item-details">
                <h3 className="item-name" onClick={() => handleProductClick(item)}>
                  {item.name}
                </h3>
                <p className="item-brand">{item.brand}</p>
                <p className="item-category">{item.category}</p>
                <div className="item-price">
                  {item.has_discount && (
                    <span className="original-price">
                      R {item.original_price?.toLocaleString()}
                    </span>
                  )}
                  <span className="current-price">
                    R {item.current_price?.toLocaleString() || item.price?.toLocaleString()}
                  </span>
                  {item.has_discount && (
                    <span className="discount-badge">
                      {item.discount_percentage}% OFF
                    </span>
                  )}
                </div>
                {item.stock_status === 'out_of_stock' && (
                  <span className="stock-status out-of-stock">Out of Stock</span>
                )}
                {item.stock_status === 'low_stock' && (
                  <span className="stock-status low-stock">Low Stock</span>
                )}
              </div>

              <div className="item-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => handleAddToCart(item)}
                  disabled={item.stock_status === 'out_of_stock'}
                >
                  {item.stock_status === 'out_of_stock' ? 'Out of Stock' : 'Add to Cart'}
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleRemoveFromWishlist(item.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="wishlist-footer">
          <div className="wishlist-summary">
            <div className="summary-item">
              <span className="summary-label">Total Items:</span>
              <span className="summary-value">{wishlist.items.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Value:</span>
              <span className="summary-value">
                R {wishlist.items.reduce((total, item) => {
                  const price = item.current_price || item.price || 0;
                  return total + price;
                }, 0).toLocaleString()}
              </span>
            </div>
          </div>
          
          <div className="wishlist-footer-actions">
            <button 
              onClick={() => {
                if (window.confirm('Add all items to cart?')) {
                  wishlist.items.forEach(item => {
                    if (item.stock_status !== 'out_of_stock') {
                      addToCart(item);
                    }
                  });
                  clearWishlist();
                }
              }}
              className="btn btn-primary"
              disabled={wishlist.items.every(item => item.stock_status === 'out_of_stock')}
            >
              Add All to Cart
            </button>
            <button onClick={() => navigate('/products')} className="btn btn-secondary">
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
